import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNil, isNotNil, range} from 'ramda';

import {SalesLeagueBranchBoardResponse, SalesLeagueBranchRow} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SalesLeaguePodium} from '../../../components/SalesLeaguePodium';
import {
  SalesLeaguePodiumEntry,
  SalesLeaguePodiumStat,
} from '../../../components/SalesLeaguePodiumCard';
import {
  SALES_LEAGUE_PODIUM_PLACES,
  SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE,
  SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX,
  SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE,
} from '../../../constants/SALES_LEAGUE_PODIUM';
import {formatMarginAmount} from '../../../utils/formatMarginAmount';
import {SalesLeagueBranchBadge} from './SalesLeagueBranchBadge';

interface SalesLeagueBranchBoardPodiumProps extends RequiredTestIdProps {
  branchBoard: SalesLeagueBranchBoardResponse;
  periodLabel: string;
}

/**
 * The top three by rank. Every branch is returned, including those that sold nothing, so a
 * place is unfilled only when the tenant has fewer branches than the podium has steps — a
 * zero-sale branch keeps its medal and shows the zeros the backend sent.
 *
 * Margin is gated per row on `totalMargin`, which is null only when margin is hidden from
 * this viewer; `marginPerVehicle` is also null for a branch that sold nothing, so it cannot
 * tell the two apart. See docs/adr/0001-margin-visibility-is-gated-at-two-levels.md.
 */
export function SalesLeagueBranchBoardPodium(props: SalesLeagueBranchBoardPodiumProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  const vehiclesLabel = i18n.t('page.salesLeague.podium.columnVehicles');
  const marginStatLabels = [
    i18n.t('page.salesLeague.table.columnMarginPerVehicle'),
    i18n.t('page.salesLeague.table.columnTotalMargin'),
  ];

  const isMarginVisibleForAnyBranch = props.branchBoard.rows.some((row) =>
    isNotNil(row.totalMargin)
  );

  const statLabels = isMarginVisibleForAnyBranch
    ? [vehiclesLabel, ...marginStatLabels]
    : [vehiclesLabel];

  const buildEntry = (
    row: SalesLeagueBranchRow | Nullish,
    place: number
  ): SalesLeaguePodiumEntry | Nullish => {
    if (isNil(row)) {
      return undefined;
    }

    const vehiclesSold = formatNumber(row.vehiclesSold) ?? EMPTY_PLACEHOLDER;

    const marginStats: SalesLeaguePodiumStat[] = isNotNil(row.totalMargin)
      ? [
          {
            label: i18n.t('page.salesLeague.table.columnMarginPerVehicle'),
            value: formatMarginAmount(
              row.marginPerVehicle,
              props.branchBoard.currency,
              formatCurrency
            ),
          },
          {
            label: i18n.t('page.salesLeague.table.columnTotalMargin'),
            value: formatMarginAmount(row.totalMargin, props.branchBoard.currency, formatCurrency),
          },
        ]
      : [];

    return {
      leadingVisual: (
        <SalesLeagueBranchBadge
          branchId={row.branchId}
          branchCode={row.branchCode}
          branchName={row.branchName}
          size={
            place === SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX
              ? SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE
              : SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE
          }
          data-testid={suffixTestId('podiumBranch', props)}
        />
      ),
      title: row.branchName,
      subtitle: i18n.t('page.salesLeague.podium.branchSubtitle', {
        vehicles: vehiclesSold,
        reps: formatNumber(row.sellers) ?? EMPTY_PLACEHOLDER,
      }),
      stats: [{label: vehiclesLabel, value: vehiclesSold}, ...marginStats],
    };
  };

  return (
    <SalesLeaguePodium
      entries={range(0, SALES_LEAGUE_PODIUM_PLACES).map((place) =>
        buildEntry(props.branchBoard.rows[place], place)
      )}
      periodLabel={props.periodLabel}
      unfilledSubtitle={i18n.t('page.salesLeague.podium.unfilledBranch')}
      unfilledStatLabels={statLabels}
      data-testid={props['data-testid']}
    />
  );
}
